import { ref, readonly } from 'vue';

let info = null;

let isMobile, isTablet, isDesktop;
let isIOS, isAndroid;

let isBrowserSupported, isWebGLSupported;

function parseUserAgent() {
    const ua = navigator?.userAgent || '';

    const isIPad = /iPad/i.test(ua);
    const isIPhoneOrIPod = /iPhone|iPod/i.test(ua);
    const isAndroidDevice = /Android/i.test(ua);
    const isMobileUA = /Mobi|Mobile|iPhone|iPod/i.test(ua);

    // iPadOS 13+ can report as Mac; detect via touch points.
    const isIPadOS = navigator?.platform === 'MacIntel' && (navigator?.maxTouchPoints || 0) > 1;

    const osName = isIPad || isIPhoneOrIPod || isIPadOS ? 'iOS' : isAndroidDevice ? 'Android' : 'Unknown';

    let deviceType = 'desktop';
    if (isIPad || isIPadOS || /Tablet/i.test(ua) || (isAndroidDevice && !isMobileUA)) {
        deviceType = 'tablet';
    } else if (isMobileUA || isAndroidDevice) {
        deviceType = 'mobile';
    }

    return {
        device: { type: deviceType },
        os: { name: osName },
        ua,
    };
}

export function useDeviceInfo() {
    if (!info) {
        try {
            info = parseUserAgent();
            console.log(`Detected ${info?.os?.name} ${info?.device?.type} (${info?.ua})`);
        } catch (error) {
            console.error('Detecting device info', error);
            throw new Error('Failed to detect device info', { cause: error });
        }

        isMobile = readonly(ref(info?.device?.type === 'mobile'));
        isTablet = readonly(ref(info?.device?.type === 'tablet'));
        isDesktop = readonly(ref(!info?.device?.type || info?.device?.type === 'desktop'));

        isIOS = readonly(ref(info?.os?.name === 'iOS'));
        isAndroid = readonly(ref(info?.os?.name === 'Android'));

        isBrowserSupported = readonly(
            ref(
                (() => {
                    try {
                        // Check for Promises and Fetch API
                        if (typeof Promise === 'undefined') {
                            return false;
                        }
                        if (typeof fetch !== 'function') {
                            return false;
                        }
                        // Check for Object and Array methods used by modern frameworks
                        if (!('assign' in Object)) {
                            return false;
                        }
                        if (!Array.prototype.find || !Array.prototype.includes) {
                            return false;
                        }
                        // Check for ES6+ features required by Vite/Vue 3
                        if (typeof Map === 'undefined' || typeof Set === 'undefined') {
                            return false;
                        }
                        if (typeof Symbol === 'undefined') {
                            return false;
                        }
                        // Check for canvas support
                        const canvas = document.createElement('canvas');
                        if (!canvas.getContext) {
                            return false;
                        }

                        return true;
                    } catch (error) {
                        console.error('Error checking browser support', error);
                        return false;
                    }
                })(),
            ),
        );

        isWebGLSupported = readonly(
            ref(
                (() => {
                    const canvas = document.createElement('canvas');
                    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
                    return !gl || !!window.WebGLRenderingContext;
                })(),
            ),
        );
    }

    return {
        isMobile,
        isTablet,
        isDesktop,

        isBrowserSupported,
        isWebGLSupported,

        isIOS,
        isAndroid,
    };
}
